// Public analytics beacon (SPEC-08 §3.2 / Q-17). Same-origin POST, no cookies,
// no identity. Unknown event types are dropped by the server.

const ENDPOINT = "/api/public/events";
const newId = () => typeof crypto !== "undefined" && crypto.randomUUID
  ? crypto.randomUUID()
  : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      return (c === "x" ? r : (r & 3 | 8)).toString(16);
    });
const requestId = newId();

function searchId() {
  const current = document.querySelector(".catalog[data-search-id]")?.dataset.searchId;
  if (current) {
    try { sessionStorage.setItem("apoku_search", JSON.stringify({ id: current, at: Date.now() })); } catch {}
    return current;
  }
  try {
    const saved = JSON.parse(sessionStorage.getItem("apoku_search") || "null");
    if (saved?.id && Date.now() - saved.at < 30 * 60 * 1000) return saved.id;
    sessionStorage.removeItem("apoku_search");
  } catch {}
  return undefined;
}

function pageKeyFromPath(pathname) {
  const app = pathname.match(/^\/apps\/[a-z0-9-]+-(\d+)\/?$/);
  if (app) return app[1];
  const category = pathname.match(/^\/categories\/([a-z0-9-]+)\/?$/);
  if (category) return category[1];
  const useCase = pathname.match(/^\/use-cases\/([a-z0-9-]+)\/?$/);
  if (useCase) return useCase[1];
  const developer = pathname.match(/^\/developers\/([a-z0-9_-]+)\/?$/);
  if (developer) return developer[1];
  return undefined;
}

function pageContext() {
  const pageType = [...document.body.classList]
    .find((name) => name.startsWith("public--"))
    ?.slice("public--".length) || "";
  const market = new URLSearchParams(window.location.search).get("market") || "";
  return {
    page_type: pageType || undefined,
    market: market || undefined,
    page_key: pageKeyFromPath(window.location.pathname),
  };
}

export function trackEvent(eventType, payload = {}) {
  if (typeof document === "undefined") return;
  if (!document.body?.classList.contains("public")) return;
  const body = JSON.stringify({
    schema_version: 1,
    event_id: newId(),
    request_id: requestId,
    search_id: searchId(),
    client_surface: "browser",
    event_type: eventType,
    ...pageContext(),
    payload,
  });
  try {
    const blob = new Blob([body], { type: "application/json" });
    if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, blob)) return;
  } catch {
    // Fall through to fetch.
  }
  try {
    fetch(ENDPOINT, {
      method: "POST",
      body,
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      credentials: "same-origin",
    }).catch(() => {});
  } catch {
    // Analytics must never break the page.
  }
}

function observeWebVitals() {
  if (typeof PerformanceObserver === "undefined") return;
  const send = (name, value) => {
    if (!Number.isFinite(value)) return;
    trackEvent("web_vital", { name, value: Math.round(value * 1000) / 1000 });
  };
  try {
    const lcp = new PerformanceObserver((list) => {
      const last = list.getEntries().at(-1);
      if (last) send("LCP", last.startTime);
    });
    lcp.observe({ type: "largest-contentful-paint", buffered: true });
  } catch {
    // Browser without LCP.
  }
  try {
    let cls = 0;
    const layout = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!entry.hadRecentInput) cls += entry.value;
      }
      send("CLS", cls);
    });
    layout.observe({ type: "layout-shift", buffered: true });
  } catch {
    // Browser without layout-shift.
  }
  try {
    const inp = new PerformanceObserver((list) => {
      const last = list.getEntries().at(-1);
      if (last) send("INP", last.duration);
    });
    inp.observe({ type: "event", buffered: true, durationThreshold: 16 });
  } catch {
    // Browser without Event Timing.
  }
}

export function enhanceAnalytics() {
  if (typeof document === "undefined") return;
  if (!document.body?.classList.contains("public")) return;

  const pageType = [...document.body.classList]
    .find((name) => name.startsWith("public--"))
    ?.slice("public--".length);
  if (pageType === "category" || pageType === "use_case") {
    trackEvent("section_viewed", { section: "page" });
  }

  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const card = target.closest(".app-card__link, .app-card__cta");
    if (card) {
      trackEvent("search_result_opened", { position: Number(card.dataset.position) || 0, source: "catalog" });
      return;
    }
    const filter = target.closest(".filters__option");
    if (filter) {
      trackEvent("filter_changed", { filter: filter.getAttribute("data-filter") || "catalog_filter" });
      return;
    }
    const store = target.closest(".app-id__cta a");
    if (store) {
      trackEvent("app_store_clicked", { store: store.href.includes("play.google") ? "gplay" : "apple" });
    }
  });

  if (typeof IntersectionObserver !== "undefined") {
    const seen = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const node = entry.target;
          const id =
            node.id ||
            node.querySelector?.("h2[id]")?.id ||
            "";
          if (!id || seen.has(id)) continue;
          seen.add(id);
          trackEvent("section_viewed", { section: id });
        }
      },
      { threshold: 0.35 },
    );
    document.querySelectorAll(".app-section, h2[id]").forEach((node) => observer.observe(node));
  }

  if (Math.random() < 0.01) observeWebVitals();
}
